
import { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getCloudflareTrafficStats, CloudflareTimeRange, CloudflareMetricSeries, CloudflareTrafficStats } from '@/services/cloudflareService';
import { getCloudflareUnavailableMessage } from '@/services/cloudflareStatus';
import { useIsMobile } from '@/hooks/use-mobile';
import { Eye, Globe, RefreshCw, Server, Users, Waypoints } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n';

type TrafficMetricKey = 'pageViews' | 'visits' | 'uniqueVisitors';

const METRIC_COLORS: Record<TrafficMetricKey, string> = {
  pageViews: '#3b82f6',
  visits: '#10b981',
  uniqueVisitors: '#f59e0b',
};

function TrafficMetricCard({
  title,
  definition,
  value,
  rangeLabel,
  icon: Icon,
  iconClassName,
}: {
  title: string;
  definition: string;
  value: string;
  rangeLabel: string;
  icon: typeof Eye;
  iconClassName: string;
}) {
  return (
    <div className="flex flex-col p-4 border rounded-lg bg-background">
      <div className="flex items-center mb-2">
        <div className={`mr-2 p-2 rounded-full ${iconClassName}`}>
          <Icon size={16} />
        </div>
        <span className="text-sm font-medium text-muted-foreground">{title}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{definition}</p>
      <span className="text-xs text-muted-foreground mt-2">{rangeLabel}</span>
    </div>
  );
}

function formatNumber(num: number) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

function buildTrendData(series: CloudflareMetricSeries) {
  return series.data.map((entry) => ({
    date: entry.date,
    value: entry.value,
  }));
}

const CloudflareAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<CloudflareTimeRange>('7d');
  const [data, setData] = useState<CloudflareTrafficStats | null>(null);
  const [unavailableMessage, setUnavailableMessage] = useState<string | null>(null);
  const [activeTrend, setActiveTrend] = useState<TrafficMetricKey>('pageViews');
  const isMobile = useIsMobile();
  const { t } = useLanguage();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setUnavailableMessage(null);
      
      const stats = await getCloudflareTrafficStats(timeRange);
      setData(stats);
    } catch (error: unknown) {
      console.warn('Cloudflare analytics unavailable:', error);
      setData(null);
      setUnavailableMessage(getCloudflareUnavailableMessage(
        error,
        t('cloudflareUnavailableDescription'),
        t('cloudflareCorsMessage'),
      ));
    } finally {
      setLoading(false);
    }
  }, [timeRange, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rangeLabel = t('cloudflareLastRange', { range: timeRange });
  const dateWindowLabel = data?.window
    ? t('cloudflareDateWindow', { start: data.window.startDate, end: data.window.endDate })
    : null;

  const metricConfig: Record<TrafficMetricKey, { label: string; definition: string; series: CloudflareMetricSeries | undefined; emptyLabel: string }> = {
    pageViews: {
      label: t('cloudflarePageViews'),
      definition: t('cloudflarePageViewsDefinition'),
      series: data?.pageViews,
      emptyLabel: t('cloudflareNoPageViews'),
    },
    visits: {
      label: t('cloudflareVisits'),
      definition: t('cloudflareVisitsDefinition'),
      series: data?.visits,
      emptyLabel: t('cloudflareNoVisits'),
    },
    uniqueVisitors: {
      label: t('cloudflareUniqueVisitors'),
      definition: t('cloudflareUniqueVisitorsDefinition'),
      series: data?.uniqueVisitors,
      emptyLabel: t('cloudflareNoUniqueVisitors'),
    },
  };

  return (
    <Card className="bg-background border border-border/50 shadow-subtle">
      <CardHeader className="p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <CardTitle>{t('cloudflareTitle')}</CardTitle>
            <CardDescription>
              {t('cloudflareDescription')}
            </CardDescription>
            <p className="mt-2 text-xs text-muted-foreground">{t('cloudflareMetricSource')}</p>
            {dateWindowLabel && (
              <p className="mt-1 text-xs text-muted-foreground">{dateWindowLabel}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Tabs
              value={timeRange}
              onValueChange={(value) => setTimeRange(value as CloudflareTimeRange)}
              className="w-full sm:w-auto"
            >
              <TabsList className="grid grid-cols-4 w-full sm:w-auto">
                <TabsTrigger value="1d">1D</TabsTrigger>
                <TabsTrigger value="7d">7D</TabsTrigger>
                <TabsTrigger value="30d">30D</TabsTrigger>
                <TabsTrigger value="60d">60D</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={fetchData} 
              title={t('cloudflareRefresh')}
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-3 sm:p-6 pt-0">
        {loading ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col gap-2">
                  <Skeleton className="w-full h-8" />
                  <Skeleton className="w-3/4 h-4" />
                </div>
              ))}
            </div>
            <Skeleton className="w-full h-[300px]" />
          </div>
        ) : unavailableMessage ? (
          <div className="flex items-center justify-center min-h-[220px] rounded-lg border border-dashed bg-muted/20 p-6 text-center">
            <div className="flex max-w-md flex-col items-center gap-2 text-muted-foreground">
              <Globe size={32} />
              <p className="font-medium text-foreground">{t('cloudflareUnavailableTitle')}</p>
              <p className="text-sm">{unavailableMessage}</p>
            </div>
          </div>
        ) : data ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
              <TrafficMetricCard
                title={t('cloudflarePageViews')}
                definition={t('cloudflarePageViewsDefinition')}
                value={formatNumber(data.pageViews.all)}
                rangeLabel={rangeLabel}
                icon={Eye}
                iconClassName="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400"
              />
              <TrafficMetricCard
                title={t('cloudflareVisits')}
                definition={t('cloudflareVisitsDefinition')}
                value={formatNumber(data.visits.all)}
                rangeLabel={rangeLabel}
                icon={Waypoints}
                iconClassName="bg-emerald-100 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-400"
              />
              <TrafficMetricCard
                title={t('cloudflareUniqueVisitors')}
                definition={t('cloudflareUniqueVisitorsDefinition')}
                value={formatNumber(data.uniqueVisitors.all)}
                rangeLabel={rangeLabel}
                icon={Users}
                iconClassName="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400"
              />
            </div>

            <div className="mb-6 p-4 border rounded-lg border-dashed bg-muted/10">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 p-2 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400">
                  <Server size={16} />
                </div>
                <div>
                  <p className="text-sm font-medium">{t('cloudflareInfrastructureRequests')}</p>
                  <p className="text-2xl font-bold mt-1">{formatNumber(data.requests.all)}</p>
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                    {t('cloudflareInfrastructureDefinition')}
                  </p>
                  <span className="text-xs text-muted-foreground mt-2 inline-block">{rangeLabel}</span>
                </div>
              </div>
            </div>
            
            <Tabs value={activeTrend} onValueChange={(value) => setActiveTrend(value as TrafficMetricKey)} className="w-full">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-medium">{t('cloudflareTrendsTitle')}</p>
                <TabsList className="grid grid-cols-3 w-full sm:w-auto">
                  <TabsTrigger value="pageViews">{t('cloudflarePageViews')}</TabsTrigger>
                  <TabsTrigger value="visits">{t('cloudflareVisits')}</TabsTrigger>
                  <TabsTrigger value="uniqueVisitors">{t('cloudflareUniqueVisitors')}</TabsTrigger>
                </TabsList>
              </div>

              {(['pageViews', 'visits', 'uniqueVisitors'] as TrafficMetricKey[]).map((metricKey) => {
                const series = metricConfig[metricKey].series;
                const chartData = series ? buildTrendData(series) : [];

                return (
                <TabsContent key={metricKey} value={metricKey} className="pt-4">
                  {chartData.length > 0 ? (
                    <div className="h-[300px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={chartData}
                          margin={{
                            top: 5,
                            right: 20,
                            left: isMobile ? 0 : 20,
                            bottom: 30,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                            }}
                          />
                          <YAxis 
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => formatNumber(value)}
                          />
                          <Tooltip 
                            formatter={(value: number) => [formatNumber(value), metricConfig[metricKey].label]}
                            labelFormatter={(label) => {
                              const date = new Date(label);
                              return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke={METRIC_COLORS[metricKey]}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-[200px]">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Globe size={32} />
                        <p>{metricConfig[metricKey].emptyLabel}</p>
                      </div>
                    </div>
                  )}
                </TabsContent>
              );
              })}
            </Tabs>

            {data.topCountries.length > 0 && (
              <div className="mt-6">
                <p className="text-sm font-medium mb-3">{t('cloudflareTopCountries')}</p>
                <div className="h-[260px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={data.topCountries.slice(0, 10)}
                      margin={{
                        top: 5,
                        right: 20,
                        left: isMobile ? 0 : 20,
                        bottom: 50,
                      }}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis 
                        type="category" 
                        dataKey="country" 
                        tick={{ fontSize: 12 }} 
                        width={isMobile ? 60 : 100}
                      />
                      <Tooltip 
                        formatter={(value: number) => [formatNumber(value), t('cloudflareRequests')]}
                      />
                      <Legend />
                      <Bar dataKey="requests" name={t('cloudflareRequests')} fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-[300px]">
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <Globe size={32} />
              <p>{t('cloudflareNoData')}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CloudflareAnalytics;
